import {
  createSelector,
} from 'reselect';
import {
  DateTime,
} from 'luxon';

import {
  getProfile,
} from './profile';
import {
  getResourceIds,
  getResourceMap,
  getResourceById,
  getResourceMappedList,
} from './resources';
import {
  onlyUniqueId,
  alphSortByName,
  alphSortSearchOptions,
} from './utils';

import type {
  User,
  ResourceType,
  ActivityLog,
  WorkoutEvent,
  BasicNotification,
} from '../types';

export const createClientSelectOption = (user: User) => ({
  label: `${user.user_profile.first_name} ${user.user_profile.last_name}`,
  value: String(user.id),
});

export const getSearchOptions = (resourceType: ResourceType, list?: string) => createSelector(
  getResourceMappedList(resourceType, list),
  (options) => {
    switch (resourceType) {
      case 'clients':
      case 'archivedClients':
        return options
          .filter(item => !item.is_pro)
          .map(option => ({
            label: `${option.user_profile.first_name} ${option.user_profile.last_name}`,
            value: String(option.id),
            rest: option,
          }))
          .sort(alphSortSearchOptions);
      default:
        return options
          .map(({ id, name }) => ({
            label: name,
            value: String(id),
          }))
          .sort(alphSortSearchOptions);
    }
  },
);

export const getCalendarClients = createSelector(
  [
    getResourceIds('clients'),
    getResourceMap('clients'),
    getProfile,
  ],
  (ids = [], map, profile) => {
    const profileOption = createClientSelectOption(profile);

    const otherClients = ids
      .filter(id => id !== profile.id)
      .map(id => createClientSelectOption(map[id]))
      .sort(alphSortSearchOptions);

    return [profileOption, ...otherClients];
  }
);

export const getClientTemplates = (
  clientId: string | number,
  resourceType: ResourceType,
) => createSelector(
  getResourceMappedList(resourceType, `client_${clientId}`),
  (templates) => {
    switch (resourceType) {
      case 'personalWorkouts':
        return templates
          .filter(w => !w.is_for_single_exercise && !w.is_practice)
          .sort(alphSortByName);
      case 'personalPrograms':
        return templates
          .sort(alphSortByName);
      case 'documents':
        return templates
          .filter(onlyUniqueId)
          .sort(alphSortByName);
      default:
        return templates
          .sort(alphSortByName);
    }
  },
);

export const getClientInfo = (clientId: number | string) => createSelector(
  [
    getResourceById('clients', clientId),
    getResourceMappedList('videos', `${clientId}`),
    getProfile,
  ],
  (
    clientInfo,
    videos,
    profile,
  ) => ({
    ...(profile.id === clientId
      ? profile
      : clientInfo
    ),
    videos,
  }),
);

export const getSearchClientOptions = (resourceType: ResourceType, list = 'all') => createSelector(
  getResourceMappedList(resourceType, list),
  options => options.map(
    option => ({
      label: option.name,
      value: String(option.id),
    }),
  ).sort(alphSortSearchOptions),
);

const assigneesSelectors = {};
export const getTemplateAssignees = (listName: string) => {
  if (assigneesSelectors[listName]) {
    return assigneesSelectors[listName];
  }

  assigneesSelectors[listName] = (
    createSelector(
      [
        getProfile,
        getResourceMappedList('clients', 'clientList'),
        getResourceMappedList('clientGroups'),
        getResourceMappedList('assignees', listName),
        getResourceMappedList('groupAssignments', listName),
      ],
      (
        me,
        clients,
        clientGroups,
        assignees,
        groupAssignments,
      ) => {
        const assigned = (client) => ({
          ...client,
          ...(assignees.some(a => a.person_id === client.id)
            ? {
              ...assignees.find(a => a.person_id === client.id),
              is_assigned: true,
            } : {
              is_assigned: false,
            }
          ),
          is_client: true,
          avatar: client.user_profile?.avatar?.thumb?.url,
          name: `${client.user_profile.first_name} ${client.user_profile.last_name}`,
        })

        return {
          groups: (
            clientGroups
              .map(
                (group) => {
                  let assigned = false;
                  let assignedCount = 0;
                  const assigment = groupAssignments.find(g => g.id === group.id);

                  if (assigment) {
                    assignedCount = assigment.assigned_count;
                    assigned = assignedCount > 0;
                  }

                  return {
                    ...group,
                    avatar: group.avatar?.thumb?.url,
                    is_assigned: assigned,
                    assigned_count: assignedCount,
                  };
                },
              )
              .sort(
                alphSortByName,
              )
          ),
          individuals: [
            {
              ...assigned(me),
              name: 'Me',
            },
            ...clients
              .map(assigned)
              .sort(
                alphSortByName,
              )
          ],
        };
      },
    )
  );
  return assigneesSelectors[listName];
};

export const getDocumentAssignees = (listName: string) => {
  if (assigneesSelectors[listName]) {
    return assigneesSelectors[listName];
  }
  assigneesSelectors[listName] = (
    createSelector(
      [
        getProfile('id'),
        getResourceMappedList('clients', 'clientList'),
        getResourceMappedList('clientGroups'),
        getResourceMappedList('assignees', listName),
        getResourceMappedList('groupAssignments', listName),
      ],
      (
        userId,
        clients,
        clientGroups,
        assignees,
        groupAssignments,
      ) => ({
        groups: (
          clientGroups
            .map(
              (group) => {
                let assigned = false;
                let assignedCount = 0;
                const assigment = groupAssignments.find(g => g.id === group.id);

                if (assigment) {
                  assignedCount = assigment.clients_count;
                  assigned = assignedCount > 0;
                }

                return {
                  ...group,
                  avatar: group.avatar?.thumb?.url,
                  is_assigned: assigned,
                  assigned_count: assignedCount,
                };
              },
            )
            .sort(
              alphSortByName,
            )
        ),
        individuals: (
          clients
            .map(
              client => ({
                ...client,
                ...(assignees.some(a => a.user_id === client.id)
                  ? {
                    ...assignees.find(a => a.user_id === client.id),
                    is_assigned: true,
                  } : {
                    is_assigned: false,
                  }
                ),
                ...(client.id === userId
                  ? {
                    name: 'me',
                  } : {
                    name: `${client.user_profile.first_name} ${client.user_profile.last_name}`,
                  }
                ),
                is_client: true,
                avatar: client.user_profile?.avatar?.thumb?.url,
              }),
            )
            .sort(
              alphSortByName,
            )
        ),
      }),
    )
  );
  return assigneesSelectors[listName];
};

const getTimeDifference = (createAt: DateTime, beginsAt: DateTime) => createAt.diff(beginsAt, ['months', 'days', 'hours', 'milliseconds']).toObject();

const getWorkoutEventText = (
  notification: BasicNotification,
  item: ActivityLog | WorkoutEvent,
  owner: string,
) => {
  const workoutName = item?.workout_name || 'workout';
  const createAt = DateTime.fromISO(notification.created_at);
  const beginsAt = DateTime.fromISO(notification.parent.begins_at);
  const interval = getTimeDifference(createAt, beginsAt);

  if (!interval.months && !interval.days && !interval.hours && Number(interval.milliseconds) < 1100
    && notification.key !== 'workout_event.update') {
    return `${owner} started ${workoutName}`;
  }

  if (!notification.parent?.begins_at) {
    return `${owner} scheduled ${workoutName}`;
  }

  switch (notification.key) {
    case 'workout_event.create':
      return `${owner} scheduled ${workoutName}`;
    case 'workout_event.update':
      return `${owner} updated ${workoutName}`;
    default:
      return `${owner} ${workoutName}`;
  }
};

const getActivityLogText = (
  notification: BasicNotification,
  item: ActivityLog | WorkoutEvent,
  owner: string,
) => {
  const name = item?.workout_name || 'activity';
  switch (notification.key) {
    case 'activity_log.create':
      return `${owner} logged ${name}`;
    case 'activity_log.update':
      return `${owner} updated ${name}`;
    default:
      return '';
  }
};

const getUserEventText = (notification: BasicNotification, owner: string) => {
  switch (notification.key) {
    case 'user.invitation_accepted':
      return `${owner} accepted invitation`;
    default:
      return '';
  }
};

const getActivityText = (notification: BasicNotification, item: ActivityLog | WorkoutEvent) => {
  const owner = notification.owner_full_name;

  switch (notification.trackable_type) {
    case 'User':
      return getUserEventText(notification, owner);
    case 'ActivityLog':
      return getActivityLogText(notification, item, owner);
    case 'WorkoutEvent':
      return getWorkoutEventText(notification, item, owner);
    default:
      return '';
  }
};

const getActivityTime = (notification: BasicNotification) => {
  const start = notification.trackable_type === 'WorkoutEvent'
    ? DateTime.fromISO(notification.parent?.begins_at)
    : DateTime.fromISO(notification.created_at);

  const timeUnits = ['years', 'months', 'days', 'hours', 'minutes'];
  const diff = DateTime.local().diff(start, timeUnits);

  for (const unit of timeUnits) {
    const value = diff[unit];
    if (value >= 1) {
      return `${Math.floor(value)} ${unit} ago`;
    }
  }

  return 'just now';
};

export const getClientRecentActivity = (userId: number) => createSelector(
  [
    getResourceMap('workoutEvents'),
    getResourceMap('activityLogs'),
    getResourceMappedList('notifications', String(userId)),
  ],
  (events, logs, notifications) => notifications.map(notification => ({
    key: notification.id,
    activityText: getActivityText(
      notification,
      events[notification.trackable_id] || logs[notification.trackable_id],
    ),
    time: getActivityTime(notification),
  })),
);
